import {
  Invoice_Job_Read_Nested,
  Invoice_Read,
  Invoice_Read_Nested,
  InvoiceState,
  InvoiceStateEvent,
  RateType,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';

import { AccountBasic } from '~hooks/useAccount/models';
import { DriverBasic } from '~hooks/useDrivers/models';
import { BasicEquipment } from '~hooks/useEquipment/models';
import { JobAssignmentNested, JobSummary } from '~hooks/useJob/models';
import { BasicMaterial } from '~hooks/useMaterials/models';
import { OrderBasic } from '~hooks/useOrders/models';
import { ProjectBasic } from '~hooks/useProjects/models';
import { RateBasicWithValue } from '~hooks/useRates/models';
import { SettlementBasic } from '~hooks/useSettlements/models';
import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import { SiteBasic } from '~hooks/useSites/models';
import { UserMinimal } from '~hooks/useUsers/models';
import { Nullable } from '~types/Nullable';
import { parseFiniteNumber } from '~utils/utilFunctions';

class InvoiceJobBasic {
  constructor(
    private _id: Nullable<string>,
    private _job_id: string,
    private _loads_count: number,
    private _job_start_at: Dayjs,
    private _vendor_job_assignment: Nullable<JobAssignmentNested>,
    private _driver: Nullable<UserMinimal>,
    private _discarded_at: Dayjs | null = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get jobId() {
    return this._job_id;
  }

  public get loadsCount() {
    return this._loads_count;
  }

  public get jobStartAt() {
    return this._job_start_at;
  }

  public get vendorJobAssignment() {
    return this._vendor_job_assignment;
  }

  public get driver() {
    return this._driver;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static parse(proto: Invoice_Job_Read_Nested) {
    return new InvoiceJobBasic(
      proto.id,
      proto.job_id,
      proto.loads_count,
      dayjs(proto.job_start_at),
      proto.vendor_job_assignment
        ? JobAssignmentNested.parse(proto.vendor_job_assignment)
        : null,
      proto.driver ? UserMinimal.parse(proto.driver) : null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class Invoice {
  constructor(
    private _id: Nullable<string>,
    private _state: InvoiceState,
    private _total_amount: number,
    private _order: Nullable<OrderBasic>,
    private _project: Nullable<ProjectBasic>,
    private _customer_account: Nullable<AccountBasic>,
    private _vendor_account: Nullable<AccountBasic>,
    private _equipment: Nullable<BasicEquipment>,
    private _material: Nullable<BasicMaterial>,
    private _driver: Nullable<DriverBasic>,
    private _pickup_site: Nullable<SiteBasic>,
    private _dropoff_site: Nullable<SiteBasic>,
    private _job: Nullable<InvoiceJobBasic>,
    private _invoice_id: string,
    private _rate: Nullable<RateBasicWithValue>,
    private _job_summary: Nullable<JobSummary>,
    private _invoice_line_items_total_quantity: number,
    private _rate_value: Nullable<number>,
    private _rate_type: Nullable<RateType>,
    private _truck_id: Nullable<string>,
    private _editable: boolean,
    private _order_order_ids: string[],
    private _project_names: string[],
    private _equipment_type_names: string[],
    private _material_names: string[],
    private _pickup_site_names: string[],
    private _dropoff_site_names: string[],
    private _job_job_ids: string[],
    private _equipment_external_ids: string[],
    private _total_loads_count: number,
    private _pay_start_time: Nullable<Dayjs>,
    private _pay_end_time: Nullable<Dayjs>,
    private _settlement: Nullable<SettlementBasic>,
    private _tickets_count: number,
    private _material_rate_itemized: boolean,
    private _material_rate_sell_price: Nullable<number>,
    private _state_events: InvoiceStateEvent[],
    private _approvable: boolean,
    private _unapprovable: boolean,
    private _add_on_charges_total_amount: number,
    private _add_on_charges_count: number,
    private _tax_rate: Nullable<number>,
    private _tax_pretax_amount: Nullable<number>,
    private _tax_total_amount: Nullable<number>,
    private _tax_description: Nullable<string>,
    private _site_rate_schedule: Nullable<SiteRateSchedule>,
    private _external_tax_rate_accounting_id: Nullable<string>,
    private _discarded_at: Dayjs | null = null,
    private _driver_pay_commission_percentage: NonNullable<
      Invoice_Read['driver_pay_commission_percentage']
    > | null = null,
    private _vendor_pay_commission_percentage: NonNullable<
      Invoice_Read['vendor_pay_commission_percentage']
    > | null = null,
    private _driver_commission_resolution: NonNullable<
      Invoice_Read['driver_commission_resolution']
    > | null = null,
    private _vendor_commission_resolution: NonNullable<
      Invoice_Read['vendor_commission_resolution']
    > | null = null,
    private _net_driver_rate: NonNullable<Invoice_Read['net_driver_rate']> | null = null,
    private _net_vendor_rate: NonNullable<Invoice_Read['net_vendor_rate']> | null = null,
    private _driver_commission_locked: NonNullable<
      Invoice_Read['driver_commission_locked']
    > | null = null,
    private _vendor_commission_locked: NonNullable<
      Invoice_Read['vendor_commission_locked']
    > | null = null,
    private _inherited_driver_pay_commission_percentage: NonNullable<
      Invoice_Read['inherited_driver_pay_commission_percentage']
    > | null = null,
    private _inherited_vendor_pay_commission_percentage: NonNullable<
      Invoice_Read['inherited_vendor_pay_commission_percentage']
    > | null = null,
  ) {}

  public get driverCommissionLocked() {
    return this._driver_commission_locked;
  }
  public get vendorCommissionLocked() {
    return this._vendor_commission_locked;
  }
  public get driverPayCommissionPercentage() {
    return this._driver_pay_commission_percentage;
  }
  public get vendorPayCommissionPercentage() {
    return this._vendor_pay_commission_percentage;
  }
  public get inheritedDriverPayCommissionPercentage() {
    return this._inherited_driver_pay_commission_percentage;
  }
  public get inheritedVendorPayCommissionPercentage() {
    return this._inherited_vendor_pay_commission_percentage;
  }
  public get driverCommissionResolution() {
    return this._driver_commission_resolution;
  }
  public get vendorCommissionResolution() {
    return this._vendor_commission_resolution;
  }
  public get netDriverRate() {
    return this._net_driver_rate;
  }
  public get netVendorRate() {
    return this._net_vendor_rate;
  }

  public get id() {
    return this._id;
  }

  public get state() {
    return this._state;
  }

  public get totalAmount() {
    return this._total_amount;
  }

  public get order() {
    return this._order;
  }

  public get project() {
    return this._project;
  }

  public get customerAccount() {
    return this._customer_account;
  }

  public get vendorAccount() {
    return this._vendor_account;
  }

  public get equipment() {
    return this._equipment;
  }

  public get material() {
    return this._material;
  }

  public get driver() {
    return this._driver;
  }

  public get pickupSite() {
    return this._pickup_site;
  }

  public get dropoffSite() {
    return this._dropoff_site;
  }

  public get job() {
    return this._job;
  }

  public get invoiceId() {
    return this._invoice_id;
  }

  public get rate() {
    return this._rate;
  }

  public get jobSummary() {
    return this._job_summary;
  }

  public get invoiceLineItemsTotalQuantity() {
    return this._invoice_line_items_total_quantity;
  }

  public get rateValue() {
    return this._rate_value;
  }

  public get rateType() {
    return this._rate_type;
  }
  public get truckId() {
    return this._truck_id;
  }

  public get editable() {
    return this._editable;
  }

  public get orderOrderIds() {
    return this._order_order_ids;
  }

  public get projectNames() {
    return this._project_names;
  }

  public get equipmentTypeNames() {
    return this._equipment_type_names;
  }

  public get materialNames() {
    return this._material_names;
  }

  public get pickupSiteNames() {
    return this._pickup_site_names;
  }

  public get dropoffSiteNames() {
    return this._dropoff_site_names;
  }

  public get jobJobIds() {
    return this._job_job_ids;
  }

  public get equipmentExternalIds() {
    return this._equipment_external_ids;
  }

  public get totalLoadsCount() {
    return this._total_loads_count;
  }

  public get payStartTime() {
    return this._pay_start_time;
  }

  public get payEndTime() {
    return this._pay_end_time;
  }

  public get settlement() {
    return this._settlement;
  }

  public get ticketsCount() {
    return this._tickets_count;
  }

  public get materialRateItemized() {
    return this._material_rate_itemized;
  }

  public get materialRateSellPrice() {
    return this._material_rate_sell_price;
  }

  public get stateEvents() {
    return this._state_events;
  }

  public get approvable() {
    return this._approvable;
  }

  public get unapprovable() {
    return this._unapprovable;
  }

  public get addOnChargesTotalAmount() {
    return this._add_on_charges_total_amount;
  }

  public get addOnChargesCount() {
    return this._add_on_charges_count;
  }

  public get taxRate() {
    return this._tax_rate;
  }

  public get taxPretaxAmount() {
    return this._tax_pretax_amount;
  }

  public get taxTotalAmount() {
    return this._tax_total_amount;
  }

  public get taxDescription() {
    return this._tax_description;
  }

  public get siteRateSchedule() {
    return this._site_rate_schedule ?? null;
  }

  // Snapshotted accounting ID from the tax rate schedule used (for QB export).
  // The invoice does not carry the schedule itself; match this against the
  // company's tax rate schedules to recover the name / external id.
  public get externalTaxRateAccountingId() {
    return this._external_tax_rate_accounting_id ?? null;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static parse(proto: Invoice_Read) {
    return new Invoice(
      proto.id,
      proto.state,
      Number(proto.total_amount),
      proto.order ? OrderBasic.parse(proto.order) : null,
      proto.project ? ProjectBasic.parse(proto.project) : null,
      proto.customer_account ? AccountBasic.parse(proto.customer_account) : null,
      proto.vendor_account ? AccountBasic.parse(proto.vendor_account) : null,
      proto.equipment ? BasicEquipment.parse(proto.equipment) : null,
      proto.material ? BasicMaterial.parse(proto.material) : null,
      proto.driver ? DriverBasic.parse(proto.driver) : null,
      proto.pickup_site ? SiteBasic.parse(proto.pickup_site) : null,
      proto.dropoff_site ? SiteBasic.parse(proto.dropoff_site) : null,
      proto.job ? InvoiceJobBasic.parse(proto.job) : null,
      proto.invoice_id,
      proto.rate ? RateBasicWithValue.parse(proto.rate) : null,
      proto.job_summary ? JobSummary.parse(proto.job_summary) : null,
      Number(proto.invoice_line_items_total_quantity),
      isNil(proto.rate_value) ? null : Number(proto.rate_value),
      proto.rate_type ?? null,
      proto.truck_id ?? null,
      proto.editable,
      proto.order_order_ids,
      proto.project_names,
      proto.equipment_type_names,
      proto.material_names,
      proto.pickup_site_names,
      proto.dropoff_site_names,
      proto.job_job_ids,
      proto.equipment_external_ids,
      Number(proto.total_loads_count),
      proto.pay_start_time ? dayjs(proto.pay_start_time) : null,
      proto.pay_end_time ? dayjs(proto.pay_end_time) : null,
      proto.settlement ? SettlementBasic.parse(proto.settlement) : null,
      Number(proto.tickets_count),
      proto.material_rate_itemized,
      isNil(proto.material_rate_sell_price)
        ? null
        : Number(proto.material_rate_sell_price),
      proto.state_events ?? [],
      proto.approvable,
      proto.unapprovable,
      Number(proto.add_on_charges_total_amount),
      Number(proto.add_on_charges_count),
      isNil(proto.tax_rate) ? null : Number(proto.tax_rate),
      isNil(proto.tax_pretax_amount) ? null : Number(proto.tax_pretax_amount),
      isNil(proto.tax_total_amount) ? null : Number(proto.tax_total_amount),
      proto.tax_description ?? null,
      proto.site_rate_schedule ? SiteRateSchedule.parse(proto.site_rate_schedule) : null,
      proto.external_tax_rate_accounting_id ?? null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.driver_pay_commission_percentage ?? null,
      proto.vendor_pay_commission_percentage ?? null,
      proto.driver_commission_resolution ?? null,
      proto.vendor_commission_resolution ?? null,
      proto.net_driver_rate ?? null,
      proto.net_vendor_rate ?? null,
      proto.driver_commission_locked ?? null,
      proto.vendor_commission_locked ?? null,
      proto.inherited_driver_pay_commission_percentage ?? null,
      proto.inherited_vendor_pay_commission_percentage ?? null,
    );
  }
}

export class InvoiceBasic {
  public static parse(proto: Invoice_Read_Nested) {
    return new InvoiceBasic(
      proto.id,
      proto.rate_type,
      parseFiniteNumber(proto.rate_value),
      Number(proto.total_amount),
      Number(proto.add_on_charges_total_amount),
      Number(proto.add_on_charges_count),
      Number(proto.invoice_line_items_total_amount),
      proto.invoice_id,
      proto.state,
      proto.state_events,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public get id() {
    return this._id;
  }

  public get rateType() {
    return this._rate_type;
  }

  public get rateValue() {
    return this._rate_value;
  }

  public get totalAmount() {
    return this._total_amount;
  }

  public get addOnChargesTotalAmount() {
    return this._add_on_charges_total_amount;
  }

  public get addOnChargesCount() {
    return this._add_on_charges_count;
  }

  public get invoiceLineItemsTotalAmount() {
    return this._invoice_line_items_total_amount;
  }

  public get invoiceId() {
    return this._invoice_id;
  }

  public get state() {
    return this._state;
  }

  public get stateEvents() {
    return this._state_events;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    private _id: string,
    private _rate_type: Nullable<RateType>,
    private _rate_value: Nullable<number>,
    private _total_amount: number,
    private _add_on_charges_total_amount: number,
    private _add_on_charges_count: number,
    private _invoice_line_items_total_amount: number,
    private _invoice_id: string,
    private _state: InvoiceState,
    private _state_events: InvoiceStateEvent[],
    private _discarded_at: Dayjs | null = null,
  ) {}
}
